import {computed} from 'vue';
import {
    AnalyticsIcon,
    ApplicationsIcon,
    CompanyIcon,
    FindFreelancerIcon,
    FreelancerPool,
    InboxIcon,
    InviteFreelancerIcon,
    InviteTeamMemberIcon,
    InvoiceIcon,
    ManageProjectsIcon,
    SettingsIcon,
} from '@navbar-logged/common/icons';
import {useAuth, useNuxtApp, useRoute} from '#imports';
import {useFeatureFlag} from '@malt/feature-flags';
import type {OrganizationMemberDetails} from '@malt/nuxt-auth-module/models';
import type {NavigationTabsRecord} from './navigation.types';
import {useNotificationsStore} from '../../../notifications/notifications.store';
import {useUserStore} from '../../../user/user.store';
import type {NavigationTab} from '../../aside-menu.types';
import {useFilteredTabs} from '../useFilteredTabs';

export function useClientNavigationTabs(): NavigationTabsRecord {
    const {$i18next} = useNuxtApp();
    const isPurchaseOrderMonitoringEnabled = useFeatureFlag('purchase-order-monitoring-in-project-client');
    const isAtsLinkFirstLevelEnabled = useFeatureFlag('navbar-ats-link-first-level');

    const userStore = useUserStore();
    const {getOrganizationMemberDetails} = useAuth();
    const notificationsStore = useNotificationsStore();
    const route = useRoute();

    // - Identities
    const isAdmin = computed<boolean>(() => !!userStore.user?.options.admin || !!userStore.user?.options.platformAdmin);
    const maySeeMembers = computed<boolean>(() => !!userStore.user?.options.maySeeMembers);
    const organizationId = computed(() => userStore.selectedIdentity?.organizationId);
    const canWorkWithAgencies = computed(() => userStore.selectedIdentity?.canWorkWithAgencies);
    const organizationMemberDetails = computed<OrganizationMemberDetails | undefined>(() =>
        organizationId.value ? (getOrganizationMemberDetails(organizationId.value) as OrganizationMemberDetails) : undefined,
    );

    const hasOrganisationPageAccess = (permissionKey: keyof OrganizationMemberDetails) => {
        return isAdmin.value || !!userStore.user?.options.platformAdmin || organizationMemberDetails.value?.[permissionKey] === true;
    };

    // - Reduced tabs

    const logoNavigationRoute = computed(() => '/');

    const allNavigationRoutes = computed((): NavigationTab[] => {
        // Error 500 bug randomly appearing https://maltcommunity.slack.com/archives/C03PWC9DPGE/p1683204841095379
        if ($i18next) {
            const messagesNavigationTab: NavigationTab = {
                id: 'Messages',
                title: $i18next.t('navbar.aside.tabs.title.messages'),
                url: {href: '/messages'},
                icons: {
                    mainIcon: InboxIcon,
                },
                notificationsCount: notificationsStore.notificationsRecord?.messages,
                isActiveWhen: ({startWith}) => startWith('/messages'),
            };

            const manageJobsNavigationItem: NavigationTab = {
                id: 'ManageJobs',
                title: $i18next.t('navbar.aside.tabs.title.manage.jobs'),
                icons: {
                    mainIcon: ManageProjectsIcon,
                },
                constraint: !isAtsLinkFirstLevelEnabled.value,
                children: [
                    {
                        id: 'FollowUpApplications',
                        title: $i18next.t('navbar.aside.tabs.title.follow.up.applications'),
                        url: {href: '/project-proposal-summaries'},
                        notificationsCount: notificationsStore.notificationsRecord?.sourcingActivities,
                        customAttrs: {'data-client-educational-pattern-applications-tour': 'true'},
                        constraint: !isAtsLinkFirstLevelEnabled.value,
                        isActiveWhen: ({startWith, matchRegXp}) =>
                            startWith('/client/sourcing-projects') ||
                            startWith('/project-proposal-summaries') ||
                            matchRegXp(/\/invite-freelancer\/.+$/),
                    },
                    {
                        id: 'Applications',
                        title: $i18next.t('navbar.aside.tabs.title.ongoing.jobs'),
                        url: {
                            href: '/project-client/?filter=owner',
                        },
                        notificationsCount: notificationsStore.notificationsRecord?.projectActions,
                        isActiveWhen: ({startWith}) =>
                            startWith('/client/missions/') || startWith('/proposal/') || startWith('/mission/') || startWith('/project-client'),
                    },
                ],
            };
            return [
                messagesNavigationTab,
                {
                    id: 'FindFreelancer',
                    title: $i18next.t('navbar.aside.tabs.title.find.freelancer'),
                    url: {href: '/project-client/find-freelancers'},
                    icons: {
                        mainIcon: FindFreelancerIcon,
                    },
                    isActiveWhen: ({startWith}) => startWith('/project-client/find-freelancers'),
                },
                {
                    id: 'FollowUpApplications',
                    title: $i18next.t('navbar.aside.tabs.title.ongoing.searches'),
                    url: {href: '/project-proposal-summaries'},
                    icons: {
                        mainIcon: ApplicationsIcon,
                    },
                    notificationsCount: notificationsStore.notificationsRecord?.sourcingActivities,
                    customAttrs: {'data-client-educational-pattern-applications-tour': 'true'},
                    constraint: isAtsLinkFirstLevelEnabled.value,
                    isActiveWhen: ({startWith, matchRegXp}) =>
                        startWith('/client/sourcing-projects') || startWith('/project-proposal-summaries') || matchRegXp(/\/invite-freelancer\/.+$/),
                },
                {
                    id: 'InviteFreelancer',
                    title: canWorkWithAgencies.value
                        ? $i18next.t('navbar.aside.tabs.title.invite.supplier')
                        : $i18next.t('navbar.aside.tabs.title.invite.freelancer'),
                    customAttrs: {'data-client-onboarding-invite-freelancer': 'true'},
                    icons: {
                        mainIcon: InviteFreelancerIcon,
                    },
                    url: {
                        href: '/invite-freelancer/?pageSource=lateral_navigation',
                    },
                    isActiveWhen: ({exact}) => exact('/invite-freelancer/'),
                },
                {
                    id: 'Applications',
                    title: $i18next.t('navbar.aside.tabs.title.ongoing.jobs'),
                    url: {
                        href: '/project-client/?filter=owner',
                    },
                    notificationsCount: notificationsStore.notificationsRecord?.projectActions,
                    constraint: isAtsLinkFirstLevelEnabled.value,
                    icons: {
                        mainIcon: ManageProjectsIcon,
                    },
                    isActiveWhen: ({startWith}) =>
                        startWith('/client/missions/') || startWith('/proposal/') || startWith('/mission/') || startWith('/project-client'),
                },
                hasOrganisationPageAccess('userMaySeeFreelancerCompliance')
                    ? {
                          id: 'HireFreelancers',
                          title: $i18next.t('navbar.aside.tabs.title.manage.freelancers'),
                          icons: {
                              mainIcon: FreelancerPool,
                          },
                          children: [
                              {
                                  id: 'FreelancerPool',
                                  title: $i18next.t('navbar.aside.tabs.title.freelancer.pool'),
                                  customAttrs: {'data-client-onboarding-freelancer-pool': 'true'},
                                  url: {
                                      href: '/client/freelancers',
                                  },
                                  isActiveWhen: ({startWith, matchRegXp}) =>
                                      startWith('/customer/freelancers') || matchRegXp(/^\/client\/freelancers(?!\/workforce).*$/),
                              },
                              {
                                  id: 'LegalDocuments',
                                  title: $i18next.t('navbar.aside.tabs.title.legal.documents'),
                                  url: {
                                      href: '/freelancer/company/workforce/',
                                  },

                                  isActiveWhen: ({startWith}) => startWith('/freelancer/company/workforce'),
                              },
                          ],
                      }
                    : {
                          id: 'HireFreelancers',
                          title: $i18next.t('navbar.aside.tabs.title.freelancer.pool'),
                          customAttrs: {'data-client-onboarding-freelancer-pool': 'true'},
                          url: {
                              href: '/client/freelancers',
                          },
                          icons: {
                              mainIcon: FreelancerPool,
                          },
                          isActiveWhen: ({startWith}) => startWith('/customer/freelancers') || startWith('/client/freelancers'),
                      },
                manageJobsNavigationItem,
                {
                    id: 'PurchaseOrder',
                    title: $i18next.t('navbar.aside.tabs.title.purchase.order'),
                    url: {href: '/project-client/purchase-order'},
                    constraint: isPurchaseOrderMonitoringEnabled.value,
                    isActiveWhen: ({startWith}) => startWith('/project-client/purchase-order'),
                },
                {
                    id: 'Monitoring',
                    title: '--',
                    constraint: hasOrganisationPageAccess('userMaySeeInvoicing') || hasOrganisationPageAccess('userMaySeeReporting'),
                    ghostChildren: [
                        {
                            id: 'Accounting',
                            title: $i18next.t('navbar.aside.tabs.title.accounting'),
                            url: {
                                href: `/invoicing/company/${userStore.selectedIdentity?.companyId}`,
                            },
                            constraint: hasOrganisationPageAccess('userMaySeeInvoicing'),
                            icons: {
                                mainIcon: InvoiceIcon,
                            },
                            isActiveWhen: ({matchRegXp}) => matchRegXp(/^\/client\/.+\/invoices/) || matchRegXp(/^\/invoicing\/company\/.+/),
                        },
                        {
                            id: 'Reporting',
                            title: $i18next.t('navbar.aside.tabs.title.reporting'),
                            constraint: hasOrganisationPageAccess('userMaySeeReporting'),
                            url: {
                                href: `/dashboard-client/analytics`,
                            },
                            icons: {
                                mainIcon: AnalyticsIcon,
                            },
                            isActiveWhen: ({startWith}) => startWith('/dashboard-client/analytics'),
                        },
                    ],
                },
                {
                    id: 'Company',
                    title: $i18next.t(`navbar.aside.tabs.title.organisation`),
                    icons: {
                        mainIcon: CompanyIcon,
                    },
                    separator: 'top',
                    children: [
                        {
                            id: 'MyTeam',
                            title: $i18next.t('navbar.aside.tabs.title.my.team'),
                            url: {href: `/client-settings/team?tab=members`},
                            constraint: maySeeMembers.value,
                            isActiveWhen: ({startWith}) => startWith('/client-settings/team') && route.query.tab === 'members',
                        },
                        {
                            id: 'InviteTeamMembers',
                            icons: {
                                mainIcon: InviteTeamMemberIcon,
                            },
                            title: $i18next.t('navbar.aside.tabs.title.invite.team.members'),
                            url: {href: `/client-settings/team?tab=inviteTeamMembers&loggedNavbar=true`},
                            constraint: maySeeMembers.value && isAtsLinkFirstLevelEnabled.value,
                            isActiveWhen: ({startWith}) => startWith('/client-settings/team') && route.query.tab === 'inviteTeamMembers',
                        },
                        {
                            id: 'CompanySettings',
                            title: $i18next.t('navbar.aside.tabs.title.company-settings'),
                            url: {
                                href: `/client-settings/`,
                            },
                            isActiveWhen: ({matchRegXp}) => matchRegXp(/\/client-settings\/(?!team\/?$).+$/),
                            notificationsCount: notificationsStore.notificationsRecord?.companySettings,
                        },
                        {
                            id: 'OrganizationSettings',
                            title: $i18next.t('navbar.aside.tabs.title.organisation-settings'),
                            url: {
                                href: `/client-settings/organization/${organizationId.value}/details`,
                            },
                            isActiveWhen: ({startWith}) => startWith('/client-settings/organization'),
                        },
                    ],
                },
                {
                    id: 'InviteTeamMembers',
                    icons: {
                        mainIcon: InviteTeamMemberIcon,
                    },
                    title: $i18next.t('navbar.aside.tabs.title.invite.team.members'),
                    url: {href: `/client-settings/team?tab=inviteTeamMembers&loggedNavbar=true`},
                    constraint: maySeeMembers.value && !isAtsLinkFirstLevelEnabled.value,
                    isActiveWhen: ({startWith}) => startWith('/client-settings/team') && route.query.tab === 'inviteTeamMembers',
                },
                {
                    id: 'AccountSettings',
                    title: $i18next.t('navbar.aside.tabs.title.account.settings'),
                    url: {href: '/account'},
                    icons: {
                        mainIcon: SettingsIcon,
                    },
                    isActiveWhen: ({startWith}) => startWith('/account'),
                },
            ];
        }
        return [];
    });

    const {filteredNavigationRoutes} = useFilteredTabs(allNavigationRoutes);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
